/**
 * SASADM-0165 — tenant white-label domain client (saas-auth SASATH-0297).
 *
 * Four calls on `/api/saas-admin/tenants/{tenantId}/whitelabel-domain`: read, put
 * (create or full replace), remove, verify. Standard `ApiResponse<T>` envelope, so
 * this uses the strict `request<ApiResponse<T>>()` + `unwrap()` pattern.
 *
 * Not configured is a 200 with `configured:false`, never a 404. `remove` and
 * `verify` answer 404 when the tenant has no domain.
 */
import { request, unwrap } from '../client';
import type {
  ApiResponse,
  PutWhitelabelDomainRequest,
  RoutingDeployOutcome,
  WhitelabelDomainDto,
  WhitelabelVerifyResponse,
  WithRoutingDeploy,
} from '../types';

const base = (tenantId: number) => `/api/saas-admin/tenants/${tenantId}/whitelabel-domain`;

export const whitelabelDomainApi = {
  /** Read the stored domain; `configured:false` when the tenant has none. */
  async get(tenantId: number): Promise<WhitelabelDomainDto> {
    const res = await request<ApiResponse<WhitelabelDomainDto>>('GET', base(tenantId));
    return unwrap(res);
  },

  /** Create or fully replace the domain + host set. Every PUT returns the domain to DRAFT. */
  async put(tenantId: number, req: PutWhitelabelDomainRequest): Promise<WhitelabelDomainDto> {
    const res = await request<ApiResponse<WhitelabelDomainDto>>('PUT', base(tenantId), {
      json: req,
    });
    return unwrap(res);
  },

  /**
   * Remove the domain and every host in its set. SASADM-0170 (SASATH-0387 §3.1) — returns
   * the routing deploy outcome from `data.routingDeploy`; a pre-0387 204/null body → null.
   */
  async remove(tenantId: number): Promise<RoutingDeployOutcome | null> {
    const res = await request<ApiResponse<WithRoutingDeploy | null> | undefined>(
      'DELETE',
      base(tenantId),
    );
    return res?.data?.routingDeploy ?? null;
  },

  /** Run the DNS check on every host; promotes DRAFT → ACTIVE when all verify. */
  async verify(tenantId: number): Promise<WhitelabelVerifyResponse> {
    const res = await request<ApiResponse<WhitelabelVerifyResponse>>(
      'POST',
      `${base(tenantId)}/verify`,
    );
    return unwrap(res);
  },
};
